import { useToast } from "@/hooks/use-toast";
import { useFeatureFlags as useFeatureFlagsHook, useToggleFeatureFlag as useToggleFeatureFlagMutation } from "@/lib/feature-flags";
import {
  Wallet,
  Heart,
  Globe,
  Bell,
  Database,
  Zap,
  Truck,
  CheckCircle,
  Languages,
  AlertCircle,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import enLocale from "@/i18n/locales/en/common.json";
import esLocale from "@/i18n/locales/es/common.json";
import soLocale from "@/i18n/locales/so/common.json";
import hmnLocale from "@/i18n/locales/hmn/common.json";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function flattenKeys(obj: Record<string, any>, prefix = ""): string[] {
  return Object.entries(obj).flatMap(([k, v]) => {
    const key = prefix ? `${prefix}.${k}` : k;
    return v !== null && typeof v === "object" && !Array.isArray(v)
      ? flattenKeys(v, key)
      : [key];
  });
}

const LOCALES = [
  { code: "en", label: "English", data: enLocale },
  { code: "es", label: "Español", data: esLocale },
  { code: "so", label: "Soomaali", data: soLocale },
  { code: "hmn", label: "Hmoob", data: hmnLocale },
];

const baseKeys = flattenKeys(enLocale);
const totalKeys = baseKeys.length;

function getTranslationCoverage() {
  return LOCALES.map(({ code, label, data }) => {
    const localeKeys = new Set(flattenKeys(data));
    const covered = baseKeys.filter((k) => localeKeys.has(k)).length;
    const missing = baseKeys.filter((k) => !localeKeys.has(k));
    return { code, label, covered, total: totalKeys, missing };
  });
}

export function ConfigDashboard({ settings }: { settings: any }) {
  const { toast } = useToast();
  const { rawFlags, isLoading: flagsLoading } = useFeatureFlagsHook();
  const toggleMutation = useToggleFeatureFlagMutation();

  const handleToggle = (key: string, currentEnabled: boolean) => {
    toggleMutation.mutate(
      { key, enabled: !currentEnabled },
      {
        onSuccess: () => {
          toast({
            title: "Feature Updated",
            description: `"${key}" has been ${currentEnabled ? "disabled" : "enabled"}.`,
          });
        },
        onError: (error: Error) => {
          toast({ title: "Error", description: error.message, variant: "destructive" });
        },
      }
    );
  };

  const coverage = getTranslationCoverage();

  const categories = rawFlags.reduce<Record<string, typeof rawFlags>>((acc, flag) => {
    const cat = flag.category || "general";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(flag);
    return acc;
  }, {});

  const categoryLabels: Record<string, string> = {
    wallet: "Wallet & Payments",
    delivery: "Delivery & Operations",
    engagement: "Community & Engagement",
    general: "General",
  };

  const categoryIcons: Record<string, any> = {
    wallet: Wallet,
    delivery: Truck,
    engagement: Heart,
    general: Zap,
  };

  return (
    <>
      <div className="grid gap-6 md:grid-cols-2">
        {flagsLoading ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">Loading feature flags...</CardContent>
          </Card>
        ) : (
          Object.entries(categories).map(([category, flags]) => {
            const IconComp = categoryIcons[category] || Zap;
            return (
              <Card key={category}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <IconComp className="w-5 h-5" />
                    {categoryLabels[category] || category}
                  </CardTitle>
                  <CardDescription>
                    {flags.filter(f => f.enabled).length} of {flags.length} features enabled
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {flags.map((flag) => (
                    <div key={flag.key} className="flex items-center justify-between gap-2 p-3 rounded-lg bg-muted/50" data-testid={`flag-toggle-${flag.key}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{flag.label}</p>
                        <p className="text-xs text-muted-foreground">{flag.description}</p>
                      </div>
                      <Switch
                        checked={flag.enabled}
                        onCheckedChange={() => handleToggle(flag.key, flag.enabled)}
                        disabled={toggleMutation.isPending}
                        data-testid={`switch-${flag.key}`}
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>
            );
          })
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Regional Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Timezone</Label>
              <Select defaultValue={settings?.timezone || "America/Chicago"}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="America/Chicago">Central Time (Chicago)</SelectItem>
                  <SelectItem value="America/New_York">Eastern Time (New York)</SelectItem>
                  <SelectItem value="America/Los_Angeles">Pacific Time (Los Angeles)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Date Format</Label>
              <Select defaultValue={settings?.dateFormat || "MM/DD/YYYY"}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                  <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                  <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Service Region</Label>
              <Input defaultValue={settings?.region || "Minneapolis-St. Paul"} />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Notification Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="p-4 rounded-lg border space-y-3">
              <div className="flex items-center justify-between gap-2">
                <Label>Email Notifications</Label>
                <Switch checked={true} />
              </div>
              <p className="text-xs text-muted-foreground">Send email alerts for critical events</p>
            </div>
            <div className="p-4 rounded-lg border space-y-3">
              <div className="flex items-center justify-between gap-2">
                <Label>SMS Notifications</Label>
                <Switch checked={false} />
              </div>
              <p className="text-xs text-muted-foreground">Send SMS for payment reminders</p>
            </div>
            <div className="p-4 rounded-lg border space-y-3">
              <div className="flex items-center justify-between gap-2">
                <Label>Push Notifications</Label>
                <Switch checked={true} />
              </div>
              <p className="text-xs text-muted-foreground">In-app push notifications</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="card-translation-coverage">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Languages className="w-5 h-5" />
            Translation Coverage
          </CardTitle>
          <CardDescription>
            {totalKeys} total keys across {LOCALES.length} supported languages
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {coverage.map(({ code, label, covered, total, missing }) => {
            const pct = Math.round((covered / total) * 100);
            const isComplete = missing.length === 0;
            return (
              <div key={code} className="space-y-1.5" data-testid={`translation-lang-${code}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    {isComplete ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-amber-500" />
                    )}
                    <span className="text-sm font-medium">{label}</span>
                    <span className="text-xs text-muted-foreground">({code})</span>
                  </div>
                  <span className="text-sm font-semibold tabular-nums">{pct}%</span>
                </div>
                <Progress value={pct} className="h-2" data-testid={`progress-coverage-${code}`} />
                <p className="text-xs text-muted-foreground">
                  {covered}/{total} keys
                  {!isComplete && (
                    <span className="ml-2 text-amber-600">
                      — {missing.length} missing: {missing.slice(0, 3).join(", ")}{missing.length > 3 ? ` +${missing.length - 3} more` : ""}
                    </span>
                  )}
                </p>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            System Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 text-center">
              <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="font-medium">Database</p>
              <p className="text-xs text-muted-foreground">Connected</p>
            </div>
            <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 text-center">
              <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="font-medium">Stripe</p>
              <p className="text-xs text-muted-foreground">Connected</p>
            </div>
            <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 text-center">
              <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="font-medium">Auth Service</p>
              <p className="text-xs text-muted-foreground">Active</p>
            </div>
            <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 text-center">
              <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="font-medium">API</p>
              <p className="text-xs text-muted-foreground">Healthy</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
